import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Search, Edit3, XCircle, Calendar, Clock, MapPin, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react'
import axios from 'axios'
import moment from 'moment-timezone'

const TIMEZONE = 'America/New_York'

const courtNames = {
  'court_1_lower_deck': 'Court 1 - Lower Deck Side',
  'court_2_lower_creek': 'Court 2 - Lower Creek Side',
  'court_3_upper_pavilion': 'Court 3 - Upper Pavilion Side',
  'court_4_upper_creek': 'Court 4 - Upper Creek Side'
}

const courtOptions = [
  { value: 'court_1_lower_deck', label: 'Court 1 - Lower Deck Side' },
  { value: 'court_2_lower_creek', label: 'Court 2 - Lower Creek Side' },
  { value: 'court_3_upper_pavilion', label: 'Court 3 - Upper Pavilion Side' },
  { value: 'court_4_upper_creek', label: 'Court 4 - Upper Creek Side' }
]

const generateTimeOptions = () => {
  const times = []
  for (let h = 6; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      const label = moment(value, 'HH:mm').format('h:mm A')
      times.push({ value, label })
    }
  }
  return times
}

const timeOptions = generateTimeOptions()

const statusColors = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-500',
  rejected: 'bg-red-100 text-red-800'
}

const ManageBooking = () => {
  const [searchParams] = useSearchParams()
  const [code, setCode] = useState(searchParams.get('code') || '')
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [looked, setLooked] = useState(false)

  // Edit modal state
  const [editingBooking, setEditingBooking] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState(null)

  // Cancel confirmation state
  const [cancellingId, setCancellingId] = useState(null)
  const [cancelLoading, setCancelLoading] = useState(false)

  const fetchBookings = async (editCode) => {
    if (!editCode || !editCode.trim()) return
    setLoading(true)
    setError(null)
    setSuccess(null)
    setLooked(true)
    try {
      const response = await axios.get(`/api/bookings/manage/${editCode.trim().toUpperCase()}`)
      setBookings(response.data)
    } catch (err) {
      setBookings([])
      setError(err.response?.data?.message || 'Could not find reservations for this code.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const urlCode = searchParams.get('code')
    if (urlCode) {
      setCode(urlCode)
      fetchBookings(urlCode)
    }
  }, [searchParams])

  const handleSubmit = (e) => {
    e.preventDefault()
    fetchBookings(code)
  }

  const openEditModal = (booking) => {
    setEditingBooking(booking)
    setEditForm({
      facility: booking.facility,
      startDate: moment(booking.startDate).tz(TIMEZONE).format('YYYY-MM-DD'),
      startTime: booking.startTime,
      endTime: booking.endTime,
      eventType: booking.eventType || ''
    })
    setEditError(null)
  }

  const handleEditSave = async () => {
    setEditLoading(true)
    setEditError(null)
    try {
      await axios.put(`/api/bookings/manage/${code.trim().toUpperCase()}/${editingBooking._id}`, editForm)
      setEditingBooking(null)
      setSuccess('Reservation updated successfully!')
      fetchBookings(code)
    } catch (err) {
      setEditError(err.response?.data?.message || 'Error updating reservation')
    } finally {
      setEditLoading(false)
    }
  }

  const handleCancel = async (bookingId) => {
    setCancelLoading(true)
    try {
      await axios.post(`/api/bookings/manage/${code.trim().toUpperCase()}/${bookingId}/cancel`)
      setCancellingId(null)
      setSuccess('Reservation cancelled successfully.')
      fetchBookings(code)
    } catch (err) {
      setError(err.response?.data?.message || 'Error cancelling reservation')
    } finally {
      setCancelLoading(false)
    }
  }

  const isPastBooking = (booking) => {
    const bookingDate = moment.tz(booking.startDate, TIMEZONE).startOf('day')
    return bookingDate.isBefore(moment.tz(TIMEZONE).startOf('day'))
  }

  const isRecurringSeries = bookings.length > 1 && bookings[0]?.recurrenceGroupId
  const activeBookings = bookings.filter(b => b.status !== 'cancelled' && b.status !== 'rejected')

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-secondary-900 via-secondary-800 to-secondary-900 text-white py-10">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/tennis" className="inline-flex items-center text-secondary-200 hover:text-white mb-4 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Tennis
          </Link>
          <h1 className="text-3xl font-bold">Manage Your Reservation</h1>
          <p className="mt-2 opacity-90">View, edit, or cancel your tennis court reservations</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Code Entry */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter your booking code
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. A1B2C3D4"
              maxLength={8}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 uppercase tracking-widest font-mono text-lg"
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="px-6 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              <Search className="w-4 h-4" />
              Look Up
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Your booking code was included in your confirmation email.
          </p>
        </form>

        {/* Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-600"></div>
          </div>
        )}

        {/* Results */}
        {!loading && looked && bookings.length > 0 && (
          <div>
            {/* Contact info header */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{bookings[0].contactName}</span>
                {bookings[0].contactEmail && <span className="ml-2 text-gray-500">({bookings[0].contactEmail})</span>}
              </p>
              {isRecurringSeries && (
                <p className="text-sm text-secondary-600 font-medium mt-1">
                  Recurring Series - {activeBookings.length} active reservation{activeBookings.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Booking cards */}
            <div className="space-y-3">
              {bookings.map((booking) => {
                const past = isPastBooking(booking)
                const canManage = !past && booking.status !== 'cancelled' && booking.status !== 'rejected'
                const courtName = courtNames[booking.facility] || booking.facility

                return (
                  <div
                    key={booking._id}
                    className={`bg-white rounded-lg shadow-sm border p-5 ${past ? 'opacity-60' : ''}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">{courtName}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[booking.status]}`}>
                            {booking.status}
                          </span>
                          {past && <span className="text-xs text-gray-400">(Past)</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {moment(booking.startDate).tz(TIMEZONE).format('ddd, MMM D, YYYY')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {moment(booking.startTime, 'HH:mm').format('h:mm A')} - {moment(booking.endTime, 'HH:mm').format('h:mm A')}
                          </span>
                        </div>
                        {booking.eventType && (
                          <p className="text-sm text-gray-500 mt-1">{booking.eventType}</p>
                        )}
                      </div>

                      {canManage && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(booking)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-secondary-700 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          {cancellingId === booking._id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-red-600">Cancel this?</span>
                              <button
                                onClick={() => handleCancel(booking._id)}
                                disabled={cancelLoading}
                                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                              >
                                {cancelLoading ? 'Cancelling...' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setCancellingId(null)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setCancellingId(booking._id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* No results */}
        {!loading && looked && bookings.length === 0 && !error && (
          <div className="text-center py-12 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No reservations found for this code.</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex justify-between items-center p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Edit Reservation</h3>
              <button
                onClick={() => setEditingBooking(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Court</label>
                <select
                  value={editForm.facility}
                  onChange={(e) => setEditForm({ ...editForm, facility: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
                >
                  {courtOptions.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  min={moment.tz(TIMEZONE).format('YYYY-MM-DD')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <select
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
                  >
                    {timeOptions.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <select
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
                  >
                    {timeOptions.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={editForm.eventType}
                  onChange={(e) => setEditForm({ ...editForm, eventType: e.target.value })}
                  placeholder="e.g. ALTA match, practice"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setEditingBooking(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-secondary-600 rounded-lg hover:bg-secondary-700 disabled:opacity-50"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageBooking
